<template>
  <ScrollableFade
    ref="scrollableFadeRef"
    content-class="scrollable-content"
    wrapper-class="bg-situational-lightgrey"
    :fade-direction="FadeDirection.FULL"
  >
    <button
      v-for="(item, idx) in timeItems"
      :key="idx"
      ref="timeListRef"
      type="button"
      class="flex min-w-[100px] shrink-0 flex-col items-center space-y-1 rounded-xl border-2 p-2 first:ml-3 last:mr-3"
      :class="{
        'js-active-time-item bg-background-neutral-secondary border-background-positive-primary':
          item.isSelected && !item.isDisabled,
        'border-transparent': !item.isSelected || item.isDisabled,
        'opacity-40': item.isDisabled && !item.isSelected,
      }"
      :aria-disabled="item.isDisabled"
      @click="handleTimeClick(item)"
    >
      <p class="text-text-neutral-primary text-base font-semibold">
        {{ item.time }}
      </p>
      <p
        v-price="item.price"
        class="whitespace-nowrap text-xs font-medium"
        :class="getPriceDiffClass(item.priceDiff)"
      ></p>
    </button>
  </ScrollableFade>
</template>

<script setup lang="ts">
  import { ref, computed, type ComponentPublicInstance } from 'vue';
  import { useHorizontalScrollToActive } from 'freedom-shared';

  import { ScrollableFade, FadeDirection } from '@/shared/ui';

  import type { TimeItem } from '../types';

  const props = defineProps<{
    timeItems: TimeItem[];
  }>();

  const emit = defineEmits<{
    onTimeClick: [item: TimeItem];
  }>();

  const scrollableFadeRef = ref<ComponentPublicInstance | null>(null);
  const timeListRef = ref<Array<HTMLButtonElement> | null>(null);

  const scrollableContentRef = computed(() => {
    return scrollableFadeRef.value?.$el?.querySelector(
      '.scrollable-content',
    ) as HTMLElement | null;
  });

  // Only trigger scroll when the selected item changes, not on every prop update
  // (e.g. price/priceDiff changes should not cause jank)
  const selectedTimeKey = computed(() => {
    const selected = props.timeItems.find(i => i.isSelected);
    return selected ? `${selected.time}-${selected.date}` : null;
  });

  useHorizontalScrollToActive(scrollableContentRef, timeListRef, {
    activeClass: 'js-active-time-item',
    items: selectedTimeKey,
  });

  const handleTimeClick = (item: TimeItem) => {
    if (item.isSelected) {
      return;
    }
    emit('onTimeClick', item);
  };

  function getPriceDiffClass(priceDiff: number | null): string {
    if (priceDiff !== null && priceDiff < 0) {
      return 'text-text-positive-primary';
    }
    if (priceDiff !== null && priceDiff > 0) {
      return 'text-text-negative-primary';
    }
    return 'text-text-neutral-quaternary';
  }
</script>
